import { lerpColor } from '../../color';
import { hsl, type World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, rgba, vignette, type Painter } from '../../art/paint';

const TAU = Math.PI * 2;
const RIDGE_STEPS = 28;
const STARS = 26;

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly a1: number;
  readonly a2: number;
  readonly light: number;
}

interface Layout {
  readonly portrait: boolean;
  readonly ridge: readonly number[];
  readonly parapet: number;
  readonly cap: number;
}

function tonesOf(look: Look): Tones {
  const pal = look.palette;
  return {
    bg: pal.bg,
    far: hsl(look.hue, 38, 16),
    ceil: look.ceil,
    ink: pal.ink,
    rim: pal.rim,
    a1: pal.held,
    a2: pal.spot,
    light: pal.incoming.circle,
  };
}

function ridgeAt(ridge: readonly number[], x: number): number {
  for (let i = 2; i < ridge.length; i += 2) {
    const x1 = ridge[i]!;
    if (x <= x1) {
      const x0 = ridge[i - 2]!;
      const t = (x - x0) / Math.max(1e-6, x1 - x0);
      return ridge[i - 1]! + (ridge[i + 1]! - ridge[i - 1]!) * t;
    }
  }
  return ridge[ridge.length - 1]!;
}

function ridgeOf(p: Painter, portrait: boolean): number[] {
  const edge = portrait ? 0.15 : 0.33;
  const top = portrait ? 0.085 : 0.16;
  const peakX = p.w * (portrait ? 0.3 : 0.36);
  const out: number[] = [];
  for (let i = 0; i <= RIDGE_STEPS; i++) {
    const x = (i / RIDGE_STEPS) * p.w;
    const d = x <= peakX ? 1 - x / peakX : (x - peakX) / (p.w - peakX);
    const shape = d * d * (3 - 2 * d);
    const shoulder = 0.02 * Math.sin((x / p.w) * 9 + 1.3) * (portrait ? 0.5 : 1);
    out.push(x, p.h * (top + (edge - top) * shape + shoulder) + p.rng.range(-1, 1) * p.unit * 0.01);
  }
  return out;
}

function layoutOf(p: Painter): Layout {
  const portrait = p.w / p.h < 0.8;
  const fieldBottom = p.cy + p.unit;
  const parapet = portrait ? Math.max(fieldBottom + 0.1 * p.unit, p.h * 0.86) : p.h * 0.87;
  return { portrait, ridge: ridgeOf(p, portrait), parapet, cap: Math.max(5, p.unit * 0.022) };
}

function sky(p: Painter, t: Tones, ridge: readonly number[]): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.55)));
  g.addColorStop(0.45, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.6)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  let peak = 0;
  for (let i = 3; i < ridge.length; i += 2) if (ridge[i]! < ridge[peak + 1]!) peak = i - 1;
  const px = ridge[peak]!;
  const py = ridge[peak + 1]!;
  const haze = lerpColor(t.ceil, t.a2, 0.3);
  const hz = ctx.createRadialGradient(px, py, 0, px, py, p.unit * 0.9);
  hz.addColorStop(0, rgba(haze, 0.26));
  hz.addColorStop(1, rgba(haze, 0));
  ctx.fillStyle = hz;
  ctx.fillRect(0, 0, p.w, p.h);
  for (let i = 0; i < STARS; i++) {
    const x = p.rng.float() * p.w;
    const top = ridgeAt(ridge, x) - 8;
    const y = p.rng.float() * top;
    const r = p.rng.range(0.6, 1.4);
    const a = p.rng.range(0.2, 0.5);
    if (top <= 0 || calmAt(p, x, y, 0.9)) continue;
    ctx.fillStyle = rgba(t.rim, a);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
  }
}

function pane(p: Painter, t: Tones, x: number, y: number, cell: number): void {
  const ctx = p.ctx;
  const ww = cell * 0.2;
  const wh = cell * 0.26;
  const lit = p.rng.chance(0.38);
  const tone = p.rng.pick([t.a1, t.a2, t.a2, t.light]);
  const a = p.rng.range(0.3, 0.5);
  if (lit && !calmAt(p, x, y, 0.95)) ctx.fillStyle = rgba(tone, a);
  else ctx.fillStyle = rgba(lerpColor(t.far, t.ink, 0.5), 0.6);
  ctx.fillRect(x, y, ww, wh);
}

function morro(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const bottom = lay.parapet;
  ctx.fillStyle = rgba(t.far);
  ctx.beginPath();
  ctx.moveTo(0, bottom);
  for (let i = 0; i < lay.ridge.length; i += 2) ctx.lineTo(lay.ridge[i]!, lay.ridge[i + 1]!);
  ctx.lineTo(p.w, bottom);
  ctx.closePath();
  ctx.fill();
  const cell = Math.max(10, p.unit * 0.055);
  let x = -cell * 0.3;
  while (x < p.w) {
    const cw = cell * p.rng.range(0.8, 1.45);
    let y = ridgeAt(lay.ridge, x + cw / 2) + p.rng.range(-0.2, 0.5) * cell;
    let first = true;
    while (y < bottom) {
      const ch = cell * p.rng.range(0.6, 0.95);
      const bx = x + p.rng.range(-0.12, 0.12) * cell;
      ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, p.rng.range(0.06, 0.32)));
      ctx.fillRect(bx, y, cw, ch);
      ctx.fillStyle = rgba(lerpColor(t.far, t.ink, 0.45), 0.7);
      ctx.fillRect(bx, y, cw, Math.max(1, cell * 0.08));
      if (first && p.rng.chance(0.14)) {
        ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.42));
        ctx.fillRect(bx + cw * 0.2, y - cell * 0.2, cell * 0.3, cell * 0.2);
      }
      if (p.rng.chance(0.55)) pane(p, t, bx + cw * p.rng.range(0.15, 0.6), y + ch * 0.32, cell);
      if (cw > cell * 1.15 && p.rng.chance(0.4)) pane(p, t, bx + cw * 0.72, y + ch * 0.32, cell);
      y += ch;
      first = false;
    }
    x += cw;
  }
  const fall = ctx.createLinearGradient(0, ridgeAt(lay.ridge, p.w * 0.5), 0, bottom);
  fall.addColorStop(0, rgba(t.bg, 0));
  fall.addColorStop(1, rgba(t.bg, 0.5));
  ctx.fillStyle = fall;
  ctx.fillRect(0, 0, p.w, bottom);
}

function cylinder(ctx: CanvasRenderingContext2D, x: number, top: number, base: number, rx: number, ry: number): void {
  ctx.beginPath();
  ctx.moveTo(x - rx, top);
  ctx.lineTo(x - rx, base);
  ctx.ellipse(x, base, rx, ry, 0, Math.PI, 0, true);
  ctx.lineTo(x + rx, top);
  ctx.ellipse(x, top, rx, ry, 0, 0, Math.PI, true);
  ctx.closePath();
}

function tank(p: Painter, t: Tones, x: number, mid: number, tw: number): void {
  const ctx = p.ctx;
  const rx = tw / 2;
  const ry = tw * 0.12;
  const th = tw * 0.72;
  const top = mid - th / 2;
  const base = mid + th / 2;
  const lw = Math.max(1.5, p.unit * 0.004);
  const body = ctx.createLinearGradient(x - rx, 0, x + rx, 0);
  body.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.3)));
  body.addColorStop(0.4, rgba(lerpColor(t.far, t.ceil, 0.7)));
  body.addColorStop(1, rgba(lerpColor(t.bg, t.ink, 0.15)));
  cylinder(ctx, x, top, base, rx, ry);
  ctx.fillStyle = body;
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.45);
  ctx.lineWidth = lw;
  for (const k of [0.36, 0.7]) {
    ctx.beginPath();
    ctx.ellipse(x, top + th * k, rx, ry, 0, 0, Math.PI);
    ctx.stroke();
  }
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.rim, 0.14));
  ctx.beginPath();
  ctx.ellipse(x, top, rx * 1.03, ry * 1.1, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.ink, 0.35));
  ctx.beginPath();
  ctx.ellipse(x - rx * 0.1, top - ry * 0.1, rx * 0.3, ry * 0.32, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.75);
  cylinder(ctx, x, top, base, rx, ry);
  ctx.stroke();
  ctx.strokeStyle = rgba(t.a2, 0.28);
  ctx.lineWidth = lw * 1.3;
  ctx.beginPath();
  ctx.moveTo(x + rx - lw * 1.6, top + ry);
  ctx.lineTo(x + rx - lw * 1.6, base - ry * 0.2);
  ctx.stroke();
}

function rebar(p: Painter, t: Tones, x: number, y: number, len: number, lean: number): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.rim, 0.2), 0.55);
  ctx.lineWidth = Math.max(1, p.unit * 0.0035);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, y - len * 0.75);
  ctx.quadraticCurveTo(x, y - len, x + lean * len * 0.3, y - len * 0.98);
  ctx.stroke();
}

function tag(p: Painter, t: Tones, x: number, y: number, s: number): void {
  const ctx = p.ctx;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.shadowBlur = s * 0.18;
  for (const [tone, dy] of [
    [t.a2, 0],
    [t.a1, s * 0.22],
  ] as const) {
    ctx.shadowColor = rgba(tone, 0.5);
    ctx.strokeStyle = rgba(tone, 0.36);
    ctx.lineWidth = Math.max(1.5, s * 0.07);
    ctx.beginPath();
    ctx.moveTo(x - s * 0.5, y + dy + s * 0.1);
    ctx.bezierCurveTo(x - s * 0.35, y + dy - s * 0.25, x - s * 0.1, y + dy + s * 0.3, x + s * 0.05, y + dy - s * 0.05);
    ctx.bezierCurveTo(x + s * 0.2, y + dy - s * 0.3, x + s * 0.3, y + dy + s * 0.25, x + s * 0.5, y + dy - s * 0.1);
    ctx.stroke();
  }
  ctx.restore();
  for (let i = 0; i < 9; i++) {
    ctx.fillStyle = rgba(p.rng.chance(0.5) ? t.a2 : t.a1, p.rng.range(0.15, 0.35));
    ctx.beginPath();
    ctx.arc(x + p.rng.range(-0.6, 0.6) * s, y + p.rng.range(-0.3, 0.5) * s, p.rng.range(0.6, 1.6), 0, TAU);
    ctx.fill();
  }
}

function castelinho(p: Painter, t: Tones, lay: Layout, x: number, mid: number, tw: number): void {
  const ctx = p.ctx;
  const bw = tw * 1.25;
  const slab = Math.max(4, tw * 0.07);
  const top = mid + tw * 0.36 + tw * 0.12 + slab;
  const bottom = lay.parapet;
  const wall = lerpColor(t.bg, t.ceil, 0.3);
  ctx.fillStyle = rgba(wall);
  ctx.fillRect(x - bw / 2, top, bw, bottom - top);
  const shade = ctx.createLinearGradient(x - bw / 2, 0, x + bw / 2, 0);
  shade.addColorStop(0, rgba(t.ink, 0.35));
  shade.addColorStop(0.6, rgba(t.ink, 0));
  shade.addColorStop(1, rgba(t.ink, 0.2));
  ctx.fillStyle = shade;
  ctx.fillRect(x - bw / 2, top, bw, bottom - top);
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.4));
  const dw = bw * 0.36;
  const dh = Math.min((bottom - top) * 0.62, tw * 1.1);
  ctx.fillRect(x - bw * 0.3, bottom - dh, dw, dh);
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ceil, 0.5));
  ctx.fillRect(x - bw / 2 - slab, top - slab, bw + slab * 2, slab);
  ctx.strokeStyle = rgba(t.ink, 0.6);
  ctx.lineWidth = Math.max(1.5, p.unit * 0.004);
  ctx.strokeRect(x - bw / 2, top, bw, bottom - top);
  ctx.strokeStyle = rgba(t.a2, 0.2);
  ctx.beginPath();
  ctx.moveTo(x + bw / 2 - 1, top + 2);
  ctx.lineTo(x + bw / 2 - 1, bottom);
  ctx.stroke();
  rebar(p, t, x - bw / 2 - slab * 0.4, top - slab, tw * 0.22, -1);
  rebar(p, t, x - bw / 2 + slab * 0.6, top - slab, tw * 0.18, 1);
  rebar(p, t, x + bw / 2 + slab * 0.4, top - slab, tw * 0.24, 1);
  tag(p, t, x + bw * 0.18, top + (bottom - top) * 0.3, bw * 0.42);
  tank(p, t, x, mid, tw);
}

function houseBox(p: Painter, t: Tones, x: number, mid: number, tw: number): void {
  const ctx = p.ctx;
  const bw = tw * 1.7;
  const top = mid + tw * 0.48;
  const bh = tw * 1.1;
  const g = ctx.createLinearGradient(0, top, 0, top + bh);
  const wall = lerpColor(t.bg, t.ceil, 0.3);
  g.addColorStop(0, rgba(wall));
  g.addColorStop(1, rgba(wall, 0));
  ctx.fillStyle = g;
  ctx.fillRect(x - bw / 2, top, bw, bh);
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ceil, 0.5));
  ctx.fillRect(x - bw / 2 - 3, top - 4, bw + 6, 4);
  rebar(p, t, x - bw / 2, top - 4, tw * 0.22, -1);
  rebar(p, t, x + bw / 2, top - 4, tw * 0.26, 1);
  tag(p, t, x + bw * 0.12, top + bh * 0.3, bw * 0.3);
  tank(p, t, x, mid, tw);
}

function parapet(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const top = lay.parapet;
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.35));
  ctx.fillRect(0, top, p.w, p.h - top);
  const bw = Math.max(18, p.unit * 0.12);
  const bh = Math.max(9, p.unit * 0.06);
  ctx.strokeStyle = rgba(t.rim, 0.05);
  ctx.lineWidth = 1;
  for (let row = 0, y = top + lay.cap; y < p.h; row++, y += bh) {
    for (let x = -(row % 2) * bw * 0.5 + p.rng.range(-0.1, 0.1) * bw; x < p.w; x += bw * p.rng.range(0.9, 1.1)) {
      ctx.strokeRect(x, y, bw * p.rng.range(0.85, 1), bh * p.rng.range(0.85, 1));
    }
  }
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ceil, 0.45));
  ctx.beginPath();
  ctx.moveTo(0, top + lay.cap);
  for (let x = 0; x <= p.w + 20; x += 20) ctx.lineTo(x, top + p.rng.range(-0.15, 0.15) * lay.cap);
  ctx.lineTo(p.w, top + lay.cap);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(t.ink, 0.35);
  ctx.fillRect(0, top + lay.cap, p.w, Math.max(1, lay.cap * 0.25));
}

function stub(p: Painter, t: Tones, lay: Layout, x: number): void {
  const ctx = p.ctx;
  const sw = Math.max(8, p.unit * 0.05);
  const sh = p.unit * 0.06;
  const top = lay.parapet - sh;
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ceil, 0.38));
  ctx.fillRect(x - sw / 2, top, sw, sh + lay.cap);
  ctx.strokeStyle = rgba(t.ink, 0.5);
  ctx.lineWidth = Math.max(1, p.unit * 0.003);
  ctx.strokeRect(x - sw / 2, top, sw, sh + lay.cap);
  rebar(p, t, x - sw * 0.25, top, p.unit * 0.08, -1);
  rebar(p, t, x + sw * 0.05, top, p.unit * 0.1, 1);
  rebar(p, t, x + sw * 0.3, top, p.unit * 0.07, 1);
}

function cabinet(p: Painter, t: Tones, x: number, y: number, cw: number, ch: number): void {
  const ctx = p.ctx;
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.5));
  ctx.fillRect(x, y, cw, ch);
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.rim, 0.15), 0.5);
  ctx.lineWidth = Math.max(1, cw * 0.03);
  ctx.strokeRect(x + 1, y + 1, cw - 2, ch - 2);
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.ink, 0.45));
  ctx.fillRect(x + cw * 0.25, y + ch * 0.09, cw * 0.5, ch * 0.12);
  const wx = x + cw / 2;
  const wy = y + ch * 0.6;
  const wr = ch * 0.3;
  const cone = ctx.createRadialGradient(wx - wr * 0.2, wy - wr * 0.2, wr * 0.1, wx, wy, wr);
  cone.addColorStop(0, rgba(t.ceil));
  cone.addColorStop(1, rgba(t.ink));
  ctx.fillStyle = cone;
  ctx.beginPath();
  ctx.arc(wx, wy, wr, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.ink, 0.2), 0.9);
  ctx.lineWidth = Math.max(1, wr * 0.16);
  ctx.beginPath();
  ctx.arc(wx, wy, wr * 0.92, 0, TAU);
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.rim, 0.2));
  ctx.beginPath();
  ctx.arc(wx, wy, wr * 0.26, 0, TAU);
  ctx.fill();
  for (let i = 0; i < 4; i++) {
    ctx.fillStyle = rgba(i % 2 === 0 ? t.a1 : t.a2, 0.45);
    ctx.beginPath();
    ctx.arc(x + cw * (0.14 + i * 0.07), y + ch * 0.9, Math.max(1, cw * 0.022), 0, TAU);
    ctx.fill();
  }
}

function paredao(p: Painter, t: Tones, lay: Layout): void {
  const cw = lay.portrait ? Math.max(0.6 * 0.16 * p.unit, 0.08 * p.w) : 0.16 * p.unit;
  const ch = cw * 0.875;
  const right = p.w - (lay.portrait ? 0.02 * p.w : 0.035 * p.unit);
  const floor = p.h - 0.02 * p.h;
  const rows = p.level >= 3 ? 3 : 2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < 2; c++) {
      cabinet(p, t, right - (c + 1) * cw, floor - (r + 1) * ch, cw, ch);
    }
  }
}

function plant(p: Painter, t: Tones, x: number, base: number, s: number): void {
  const ctx = p.ctx;
  const leaf = lerpColor(t.far, t.a1, 0.2);
  const pw = s * 0.42;
  const ph = s * 0.36;
  for (let i = 0; i < 7; i++) {
    const lx = x + (i - 3) * pw * 0.12 + p.rng.range(-0.03, 0.03) * s;
    const tall = s * p.rng.range(0.7, 1.15) * (1 - Math.abs(i - 3) * 0.08);
    const bend = (i - 3) * s * 0.05 + p.rng.range(-0.04, 0.04) * s;
    const half = s * 0.045;
    ctx.fillStyle = rgba(lerpColor(leaf, t.ink, p.rng.range(0, 0.25)));
    ctx.beginPath();
    ctx.moveTo(lx - half, base - ph);
    ctx.quadraticCurveTo(lx - half * 1.4 + bend * 0.5, base - ph - tall * 0.55, lx + bend, base - ph - tall);
    ctx.quadraticCurveTo(lx + half * 1.4 + bend * 0.5, base - ph - tall * 0.55, lx + half, base - ph);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = rgba(lerpColor(leaf, t.a1, 0.25), 0.3);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(lx, base - ph);
    ctx.quadraticCurveTo(lx + bend * 0.5, base - ph - tall * 0.55, lx + bend, base - ph - tall * 0.95);
    ctx.stroke();
  }
  ctx.fillStyle = rgba(lerpColor(t.far, t.a2, 0.2));
  ctx.beginPath();
  ctx.moveTo(x - pw / 2, base - ph);
  ctx.lineTo(x + pw / 2, base - ph);
  ctx.lineTo(x + pw * 0.38, base);
  ctx.lineTo(x - pw * 0.38, base);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.far, t.a2, 0.3));
  ctx.fillRect(x - pw * 0.55, base - ph, pw * 1.1, ph * 0.18);
  ctx.strokeStyle = rgba(t.ink, 0.6);
  ctx.lineWidth = Math.max(1, p.unit * 0.003);
  ctx.strokeRect(x - pw * 0.55, base - ph, pw * 1.1, ph * 0.18);
}

function cloth(p: Painter, t: Tones, x: number, y: number, s: number, kind: number): void {
  const ctx = p.ctx;
  const tone = lerpColor(t.far, [t.light, t.a1, t.a2, t.rim][kind % 4] ?? t.rim, 0.3);
  ctx.fillStyle = rgba(tone);
  ctx.strokeStyle = rgba(t.ink, 0.5);
  ctx.lineWidth = Math.max(1, p.unit * 0.0025);
  ctx.beginPath();
  if (kind % 3 === 0) {
    ctx.moveTo(x - s * 0.5, y);
    ctx.lineTo(x + s * 0.5, y);
    ctx.lineTo(x + s * 0.75, y + s * 0.25);
    ctx.lineTo(x + s * 0.5, y + s * 0.35);
    ctx.lineTo(x + s * 0.45, y + s * 1.05);
    ctx.lineTo(x - s * 0.45, y + s * 1.05);
    ctx.lineTo(x - s * 0.5, y + s * 0.35);
    ctx.lineTo(x - s * 0.75, y + s * 0.25);
  } else if (kind % 3 === 1) {
    ctx.moveTo(x - s * 0.45, y);
    ctx.lineTo(x + s * 0.45, y);
    ctx.lineTo(x + s * 0.5, y + s * 0.7);
    ctx.lineTo(x + s * 0.06, y + s * 0.7);
    ctx.lineTo(x, y + s * 0.3);
    ctx.lineTo(x - s * 0.06, y + s * 0.7);
    ctx.lineTo(x - s * 0.5, y + s * 0.7);
  } else {
    ctx.moveTo(x - s * 0.4, y);
    ctx.lineTo(x + s * 0.4, y);
    ctx.lineTo(x + s * 0.42, y + s * 1.2);
    ctx.lineTo(x - s * 0.38, y + s * 1.15);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = rgba(t.rim, 0.5);
  for (const dx of [-0.32, 0.32]) ctx.fillRect(x + dx * s - 1, y - s * 0.08, 2, s * 0.14);
}

function line(p: Painter, t: Tones, x0: number, y0: number, x1: number, y1: number, droop: number, s: number, count: number, keep: number): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.rim, 0.25), 0.5);
  ctx.lineWidth = Math.max(1, p.unit * 0.0025);
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.quadraticCurveTo((x0 + x1) / 2, (y0 + y1) / 2 + droop * 2, x1, y1);
  ctx.stroke();
  for (let i = 0; i < count; i++) {
    const u = (i + 0.5 + p.rng.range(-0.15, 0.15)) / count;
    const x = x0 + (x1 - x0) * u;
    const y = y0 + (y1 - y0) * u + 4 * droop * u * (1 - u);
    if (calmAt(p, x, y + s * 0.5, keep)) continue;
    cloth(p, t, x, y, s * p.rng.range(0.85, 1.1), i + p.rng.int(0, 3));
  }
}

function pole(p: Painter, t: Tones, x: number, top: number, bottom: number): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.rim, 0.15), 0.6);
  ctx.lineWidth = Math.max(1.5, p.unit * 0.005);
  ctx.beginPath();
  ctx.moveTo(x, bottom);
  ctx.lineTo(x, top);
  ctx.stroke();
}

function bulbs(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const y = lay.parapet - p.unit * 0.035;
  const gap = Math.max(14, p.unit * 0.09);
  const swag = p.unit * 0.025;
  ctx.strokeStyle = rgba(t.ink, 0.7);
  ctx.lineWidth = Math.max(1, p.unit * 0.0025);
  ctx.beginPath();
  ctx.moveTo(0, y);
  for (let x = 0; x < p.w + gap; x += gap) ctx.quadraticCurveTo(x + gap / 2, y + swag * 2, x + gap, y);
  ctx.stroke();
  let i = 0;
  for (let x = gap / 2; x < p.w; x += gap, i++) {
    const by = y + swag;
    if (calmAt(p, x, by, 1)) continue;
    const tone = i % 2 === 0 ? t.a1 : t.a2;
    glowDot(ctx, x, by + 3, p.unit * 0.03, tone, 0.2);
    ctx.fillStyle = rgba(lerpColor(tone, t.rim, 0.3), 0.55);
    ctx.beginPath();
    ctx.arc(x, by + 3, Math.max(1.5, p.unit * 0.007), 0, TAU);
    ctx.fill();
  }
}

function landscape(p: Painter, t: Tones, lay: Layout): void {
  const band = Math.max(0, (p.w - 2 * 1.08 * p.unit) / 2);
  const tw = Math.min(0.34 * p.unit, Math.max(0.22 * p.unit, band * 0.8));
  const tx = Math.max(tw / 2 + 0.03 * p.unit, band * 0.5);
  const mid = p.h * 0.42;
  castelinho(p, t, lay, tx, mid, tw);
  parapet(p, t, lay);
  stub(p, t, lay, p.w - Math.max(band * 0.82, 0.3 * p.unit));
  if (p.level >= 2) {
    const right = p.w - Math.max(band, 0.3 * p.unit);
    const x0 = right + Math.max(band, 0.3 * p.unit) * 0.12;
    const y = p.h * 0.47;
    pole(p, t, x0, y - p.unit * 0.04, lay.parapet);
    line(p, t, x0, y, p.w + 8, y - p.unit * 0.02, p.unit * 0.03, p.unit * 0.07, 3, 1);
  }
  if (p.level >= 4) bulbs(p, t, lay);
  plant(p, t, tx + tw * 0.625 + 0.12 * p.unit, lay.parapet, p.unit * 0.24);
  paredao(p, t, lay);
}

function portrait(p: Painter, t: Tones, lay: Layout): void {
  const tw = Math.min(0.36 * p.unit, Math.max(0.2 * p.unit, p.w * 0.2));
  houseBox(p, t, p.w * 0.14 + tw * 0.2, p.h * 0.28, tw);
  if (p.level >= 2) {
    const y = Math.min(p.cy + p.unit + 0.04 * p.unit, p.h * 0.84);
    pole(p, t, p.w * 0.06, y - p.unit * 0.03, lay.parapet);
    line(p, t, p.w * 0.06, y, p.w + 8, y - p.unit * 0.02, p.unit * 0.04, Math.max(14, p.unit * 0.08), 4, 1);
  }
  parapet(p, t, lay);
  if (p.level >= 4) bulbs(p, t, lay);
  plant(p, t, Math.max(p.w * 0.12, p.unit * 0.12), lay.parapet, Math.max(36, p.unit * 0.22));
  paredao(p, t, lay);
}

export function paint(p: Painter, look: Look): void {
  const t = tonesOf(look);
  const lay = layoutOf(p);
  sky(p, t, lay.ridge);
  morro(p, t, lay);
  if (lay.portrait) portrait(p, t, lay);
  else landscape(p, t, lay);
  calmCenter(p, t.bg, 0.62, 1.15);
  grain(p, 0.06, true);
  vignette(p, t.ink, 0.45);
}
